import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import {
  useDescribeStringGroupLazyQuery,
  useListStringsLazyQuery,
} from 'src/generated/graphql';
import { StringInfo } from '../types';
import {
  computeCxIdDiff,
  computeVersionDiff,
  CxIdDiff,
} from '../utils/versionDiff';
import { fetchVersionSnapshot } from '../utils/fetchVersionSnapshot';
import { describeLoadFailure } from '../utils/snapshotError';
import VersionDiffTable from './VersionDiffTable';
import CxIdDiffTable from './CxIdDiffTable';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import { joinList } from 'src/common/utils/joinList';

interface DiffModalProps {
  visible: boolean;
  version: number;
  namespaceId: string;
  stringGroupId: string;
  onDismiss: () => void;
}

const DiffModal: React.FC<DiffModalProps> = ({
  visible,
  version,
  namespaceId,
  stringGroupId,
  onDismiss,
}) => {
  const { t, locale } = useTranslation();
  const previousVersion = version - 1;

  // Two independent hooks so the previous and current snapshots can load in
  // parallel: a single useListStringsLazyQuery shares one ObservableQuery and
  // cannot service two concurrent fetches, so reusing it would force the two
  // walks to run sequentially (doubling latency) or corrupt each other.
  const [fetchPreviousStrings] = useListStringsLazyQuery();
  const [fetchCurrentStrings] = useListStringsLazyQuery();
  // Group CX IDs are version-level metadata read via describeStringGroup (a
  // fast version-aware query), separate from the ListStrings snapshot — the
  // same split ViewVersionModal uses. Two independent hooks so the previous and
  // current reads run in parallel, mirroring the string-snapshot hooks above.
  const [fetchPreviousGroup] = useDescribeStringGroupLazyQuery();
  const [fetchCurrentGroup] = useDescribeStringGroupLazyQuery();
  // Loaded diff paired with the version it was computed for, so a stale result
  // from a previous open can never be mistaken for the current request. Stored
  // together in one state update, they can't diverge.
  const [loaded, setLoaded] = useState<{
    version: number;
    // The raw snapshots, not the computed diff: diff rows carry localized field names, so a
    // locale change re-labels the table without re-fetching either version.
    previousStrings: StringInfo[];
    currentStrings: StringInfo[];
    cxIdDiff: CxIdDiff;
    // The full CX ID membership on each side, kept alongside the diff so the CX
    // IDs section can show a side-by-side "Previous vs Current" list (matching
    // the product mock) rather than only the added/removed delta.
    previousCxIds: string[];
    currentCxIds: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  // Error paired with the version it belongs to, mirroring `loaded`: on a
  // reopen for a different version the modal stays mounted and this state is
  // only cleared from the post-paint effect, so stamping the version lets the
  // render gate a previous run's error out of the new request's frame.
  //
  // Held as thrown rather than coerced to an `Error`: only a real upstream message can fill the
  // "{detail}" slot, and a stand-in would render nested inside its own frame.
  const [error, setError] = useState<{
    version: number;
    thrown: unknown;
  } | null>(null);

  useEffect(() => {
    if (!visible || previousVersion < 1) {
      return;
    }

    // A stale result from the previous open is kept out of the UI by the
    // `dataMatchesRequest` guard below (it won't match this version), so there
    // is no need to clear it here — leaving it avoids an extra render.
    setError(null);
    setLoading(true);

    let cancelled = false;

    const run = async () => {
      try {
        // Independent hooks let the two version snapshots load in parallel.
        const [previousStrings, currentStrings, previousCxIds, currentCxIds] =
          await Promise.all([
            loadSnapshot(fetchPreviousStrings, previousVersion),
            loadSnapshot(fetchCurrentStrings, version),
            loadCxIds(fetchPreviousGroup, previousVersion),
            loadCxIds(fetchCurrentGroup, version),
          ]);
        if (!cancelled) {
          setLoaded({
            version,
            previousStrings,
            currentStrings,
            cxIdDiff: computeCxIdDiff(previousCxIds, currentCxIds),
            // De-duplicated but order-preserving, mirroring ViewVersionModal —
            // the badges follow the backend's ordering rather than being
            // re-sorted.
            previousCxIds,
            currentCxIds,
          });
        }
      } catch (loadError) {
        if (!cancelled) {
          setError({
            version,
            thrown: loadError,
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    /**
     * Loads a version snapshot via the given fetch hook, then unions any string
     * that spans page boundaries — the backend may split one string's variants
     * across pages, so a plain concatenation could report the same stringId
     * twice.
     */
    const loadSnapshot = async (
      fetchFn: typeof fetchPreviousStrings,
      stringGroupVersion: number,
    ): Promise<StringInfo[]> => {
      const flat = await fetchVersionSnapshot(
        fetchFn,
        namespaceId,
        stringGroupId,
        stringGroupVersion,
      );
      const byStringId = new Map<string, StringInfo>();
      for (const stringInfo of flat) {
        mergeString(byStringId, stringInfo);
      }
      return [...byStringId.values()];
    };

    /**
     * Loads the group's CX IDs for one version via describeStringGroup. CX IDs
     * are supplementary to the string diff, so a failure here never fails the
     * whole diff — it resolves to an empty list (matching ViewVersionModal),
     * leaving the string comparison intact.
     */
    const loadCxIds = async (
      fetchGroup: typeof fetchPreviousGroup,
      stringGroupVersion: number,
    ): Promise<string[]> => {
      try {
        const result = await fetchGroup({
          fetchPolicy: 'network-only',
          variables: {
            namespaceId,
            stringGroupId,
            stringGroupVersion: stringGroupVersion.toString(),
          },
        });
        return result.data?.describeStringGroup.cxIds ?? [];
      } catch {
        return [];
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    version,
    previousVersion,
    namespaceId,
    stringGroupId,
    fetchPreviousStrings,
    fetchCurrentStrings,
    fetchPreviousGroup,
    fetchCurrentGroup,
  ]);

  // The snapshot fetches fire from a post-paint effect, so on the first render
  // after opening (or reopening for a different version) `loading` is still
  // false and `loaded` is either null or holds the previously viewed version's
  // diff. Gating on the loaded version matching the requested one keeps that
  // stale/empty snapshot out of the table until the new data arrives.
  const dataMatchesRequest = loaded !== null && loaded.version === version;
  // Only surface an error that belongs to the current request. Without this, a
  // reopen for a different version right after a failed load would flash the
  // previous version's error under the new header for the frame before the
  // effect clears it (matches `RollbackModal`'s version-gated error).
  const errorMatchesRequest = error !== null && error.version === version;
  const diffResults = useMemo(
    () =>
      loaded && dataMatchesRequest
        ? computeVersionDiff(t, loaded.previousStrings, loaded.currentStrings)
        : [],
    [t, loaded, dataMatchesRequest],
  );

  const cxIdDiff = useMemo<CxIdDiff>(
    () =>
      loaded && dataMatchesRequest
        ? loaded.cxIdDiff
        : { added: [], removed: [] },
    [loaded, dataMatchesRequest],
  );
  const previousCxIds = useMemo(
    () => (loaded && dataMatchesRequest ? loaded.previousCxIds : []),
    [loaded, dataMatchesRequest],
  );
  const currentCxIds = useMemo(
    () => (loaded && dataMatchesRequest ? loaded.currentCxIds : []),
    [loaded, dataMatchesRequest],
  );
  const hasCxIdChanges =
    cxIdDiff.added.length > 0 || cxIdDiff.removed.length > 0;

  const addedCount = useMemo(
    () => diffResults.filter((d) => d.changeType === 'added').length,
    [diffResults],
  );
  const removedCount = useMemo(
    () => diffResults.filter((d) => d.changeType === 'removed').length,
    [diffResults],
  );
  const changedCount = useMemo(
    () => diffResults.filter((d) => d.changeType === 'changed').length,
    [diffResults],
  );

  /** Each clause is one whole-sentence resource, combined through {@link joinList} rather than a
   * "{strings}; {cxIds}" frame — matching `formatChangeSummary` in `useVersionHistory.ts`. */
  const changeSummary = useMemo(() => {
    const clauses = [
      t('sgd-diff-string-counts', { addedCount, removedCount, changedCount }),
    ];
    if (hasCxIdChanges) {
      clauses.push(
        t('sgd-diff-cxid-counts', {
          addedCount: cxIdDiff.added.length,
          removedCount: cxIdDiff.removed.length,
        }),
      );
    }
    return joinList(locale, clauses);
  }, [
    t,
    locale,
    addedCount,
    removedCount,
    changedCount,
    hasCxIdChanges,
    cxIdDiff,
  ]);

  const renderContent = () => {
    if (!visible) {
      return null;
    }

    if (previousVersion < 1) {
      return <Alert type="info">{t('sgd-diff-no-previous-version')}</Alert>;
    }

    if (errorMatchesRequest) {
      // A refusal is not a load failure, so it drops the "Failed to load version
      // data:" prefix and shows the service's own wording alone.
      const failure = describeLoadFailure(t, error.thrown);
      return isAccessDenied(error.thrown) ? (
        <AccessDeniedAlert
          message={getAccessDeniedMessage(error.thrown)}
          deniedNamespace={getAccessDeniedNamespace(error.thrown)}
          data-testid="diff-access-denied"
        />
      ) : (
        <Alert type="error">
          {/* Only the service's own wording goes in the "{detail}" slot. Our
              own guard copy is already a complete sentence, so framing it would
              wrap it in a second sentence saying the same thing; a throwable
              with no wording at all gets the frameless stand-in. */}
          {failure.kind === 'upstream'
            ? t('sgd-diff-load-failed-detail', { detail: failure.text })
            : failure.kind === 'own'
              ? failure.text
              : t('sgd-diff-load-failed')}
        </Alert>
      );
    }

    if (loading || !dataMatchesRequest) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
            {t('sgd-diff-loading')}
          </Box>
        </Box>
      );
    }

    const hasStringChanges = diffResults.length > 0;

    return (
      <SpaceBetween size="m">
        <Alert type="info">
          <strong>{t('sgd-diff-change-summary')}</strong> {changeSummary}
        </Alert>

        {hasCxIdChanges && (
          <CxIdDiffTable
            previousCxIds={previousCxIds}
            currentCxIds={currentCxIds}
            previousHeader={t('sgd-diff-column-previous', {
              version: previousVersion,
            })}
            currentHeader={t('sgd-diff-column-current', { version })}
          />
        )}

        {hasStringChanges && (
          <VersionDiffTable
            results={diffResults}
            previousHeader={t('sgd-diff-column-previous', {
              version: previousVersion,
            })}
            currentHeader={t('sgd-diff-column-current', { version })}
            scrollable
          />
        )}

        {!hasStringChanges && !hasCxIdChanges && (
          <Box textAlign="center" color="inherit" padding="l">
            {t('sgd-diff-no-differences')}
          </Box>
        )}
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={t('sgd-diff-header', { previousVersion, version })}
    >
      {renderContent()}
    </Modal>
  );
};

/**
 * Merges an incoming string into the accumulator, unioning variants by cxId when the same
 * stringId spans page boundaries.
 */
function mergeString(target: Map<string, StringInfo>, incoming: StringInfo) {
  const existing = target.get(incoming.stringId);
  if (!existing) {
    target.set(incoming.stringId, incoming);
    return;
  }
  const variants = new Map(
    existing.variants.map((variant) => [variant.cxId, variant]),
  );
  incoming.variants.forEach((variant) => variants.set(variant.cxId, variant));
  target.set(incoming.stringId, {
    ...existing,
    ...incoming,
    variants: [...variants.values()],
  });
}

export default DiffModal;
